import { sagaIntl } from '@i18n';
import * as Actions from '../actions';
import * as Services from '../services';
import {
  all,
  delay,
  race,
  take,
  takeLatest,
  takeEvery,
  put,
  select,
  SagaGenerator,
} from 'typed-redux-saga/macro';
import { isEmpty } from 'lodash';
import { enqueueSnackbar } from '@common-modules/snackbar';
import {
  deleteReactionCodePayload,
  CheckRedactionCodePayload,
  RedactionCode,
  SaveRedactionCodeSuccessPayload,
} from '../models';
import {
  actionClearSelectedRedactionCode,
  CHECK_REDACTION_CODE_EXISTS_FAILURE,
  CHECK_REDACTION_CODE_EXISTS_SUCCESS,
} from '../actions';
import {
  selectLimit,
  selectOffset,
  selectAllRedactionCodeRecords,
  selectRedactionCodes,
  selectSelectedEditCode,
} from '../selectors';
import { DEFAULT_MAXIMUM_LIMIT } from '@helpers/constants';

export interface Action<P> {
  type: string;
  payload: P;
}

export function* fetchRedactionCodes() {
  yield* takeEvery(
    [
      Actions.FETCH_REDACTION_CODES,
      Actions.FETCH_ALL_REDACTION_CODES,
      Actions.ON_CHANGE_PAGE,
      Actions.ON_CHANGE_PAGE_SIZE,
    ],
    onFetchRedactionCodes
  );
}
function* onFetchRedactionCodes(action: Action<unknown>) {
  let limit, offset;

  if (Actions.FETCH_ALL_REDACTION_CODES.match(action)) {
    limit = DEFAULT_MAXIMUM_LIMIT;
    offset = 0;
  } else {
    limit = yield* select(selectLimit);
    offset = yield* select(selectOffset);
  }

  yield* put(
    Services.fetchRedactionCodes(limit, offset > 0 ? limit * offset : offset)
  );
}

/**
 * Longest a caller will wait on the code fetch before giving up on it.
 *
 * Mirrors `REDACTION_STATUS_SETTLE_TIMEOUT_MS`, and exists for a harder reason
 * than impatience: `Services.fetchRedactionCodes` returns WITHOUT dispatching
 * anything when the codes schema id cannot be resolved, so a bare
 * `take([SUCCESS, FAILURE])` would wait forever on a perfectly ordinary
 * failure.
 */
export const REDACTION_CODES_SETTLE_TIMEOUT_MS = 5_000;

/**
 * Makes sure the org's redaction codes are in the store, and returns them.
 *
 * For callers that need to RESOLVE a code id rather than render a picker — the
 * lexicon apply (VE-24948 / `U5-FR8`), which stamps an entry's configured code
 * onto the detections it creates. Those callers cannot use the components'
 * pattern of firing a fetch from an effect and re-rendering when it lands:
 * they run once, and a code that arrives after they finish arrives too late.
 *
 * The `FETCH_ALL_REDACTION_CODES` watcher lives in the always-on
 * `initMediaDetails` root saga, so this works on any route, including the
 * document editor whose own route saga forks no code watchers.
 *
 * Returns whatever the store holds when the wait ends — `{}` if the fetch
 * failed, timed out, or the org simply has no codes. The three are
 * deliberately indistinguishable HERE; a caller that must tell them apart
 * should watch the actions itself. For the lexicon apply they are the same
 * answer: stamp nothing (BR-15).
 */
export function* ensureRedactionCodesLoaded(): SagaGenerator<{
  [id: string]: RedactionCode;
}> {
  // "Have we fetched?" is a question about ALL records: an org whose codes
  // are all soft-deleted has an empty LIVE list and would re-fetch, and wait
  // out the timeout, on every apply (VE-28768).
  const allLoaded = yield* select(selectAllRedactionCodeRecords);
  if (!isEmpty(allLoaded)) {
    return yield* select(selectRedactionCodes);
  }
  yield* put(Actions.FETCH_ALL_REDACTION_CODES());
  // Safe to start taking after the put: the watcher's own dispatch goes to an
  // async thunk, so neither terminal action can have fired yet.
  yield* race({
    settled: take([
      Actions.FETCH_REDACTION_CODES_SUCCESS,
      Actions.FETCH_REDACTION_CODES_FAILURE,
    ]),
    timeout: delay(REDACTION_CODES_SETTLE_TIMEOUT_MS),
  });
  return yield* select(selectRedactionCodes);
}

export function* fetchRedactionCodesFailure() {
  yield* takeEvery(Actions.FETCH_REDACTION_CODES_FAILURE, function* (action) {
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'fetchRedactionCodesFailure',
            defaultMessage:
              'Failed to retrieve redaction codes: { responseCode }',
          },
          { responseCode: action.meta.response?.status || 'Unknown' }
        ),
      })
    );
  });
}

/**
 * Set `isDeleted` on a code rather than removing it.
 *
 * The save mutation is an upsert keyed by id — the same write the drawer
 * makes — so nothing here needs the delete mutation, which is now reserved
 * for the deleted view's permanent delete.
 */
function* setRedactionCodeDeleted(sdoId: string, isDeleted: boolean) {
  const codes = yield* select(selectAllRedactionCodeRecords);
  const redactionCode = codes[sdoId];
  if (!redactionCode) {
    // The user has just confirmed a destructive action; saying nothing is
    // indistinguishable from a hung request.
    const intl = sagaIntl();
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'redactionCodeNoLongerAvailable',
          defaultMessage: 'That redaction code is no longer available.',
        }),
        variant: 'error',
      })
    );
    return;
  }
  yield* put(
    Services.setRedactionCodeDeleted({
      redactionCode: { ...redactionCode, isDeleted },
    })
  );
}

export function* watchSetRedactionCodeDeletedSaga() {
  yield* all([
    takeLatest(
      Actions.SET_REDACTION_CODE_DELETED_SUCCESS,
      function* (action: Action<SaveRedactionCodeSuccessPayload>) {
        const intl = sagaIntl();
        // Which way the flag went, read back from what was saved — the same
        // write serves the delete and the restore.
        const wasDeleted =
          action.payload?.createStructuredData?.data?.isDeleted === true;
        yield* put(
          enqueueSnackbar({
            message: wasDeleted
              ? intl.formatMessage({
                  id: 'redactionCodeDeleted',
                  defaultMessage: 'Redaction code deleted.',
                })
              : intl.formatMessage({
                  id: 'redactionCodeRestored',
                  defaultMessage: 'Redaction code restored.',
                }),
            // No variant: the plain dark snackbar, as elsewhere for an
            // action that simply happened. Green reads as a save.
          })
        );
      }
    ),
    takeLatest(Actions.SET_REDACTION_CODE_DELETED_FAILURE, function* () {
      const intl = sagaIntl();
      yield* put(
        enqueueSnackbar({
          message: intl.formatMessage({
            id: 'savedRedactionCodeFailure',
            defaultMessage: 'An error occurred while saving redaction code.',
          }),
          variant: 'error',
        })
      );
    }),
  ]);
}

export function* watchSoftDeleteRedactCodeSaga() {
  yield* takeLatest(
    Actions.SOFT_DELETE_REDACTION_CODE_REQUEST,
    function* (action: Action<{ sdoId: string }>) {
      yield* setRedactionCodeDeleted(action.payload.sdoId, true);
    }
  );
}

export function* watchRestoreRedactCodeSaga() {
  yield* takeLatest(
    Actions.RESTORE_REDACTION_CODE_REQUEST,
    function* (action: Action<{ sdoId: string }>) {
      yield* setRedactionCodeDeleted(action.payload.sdoId, false);
    }
  );
}

export function* watchDeleteRedactCodeSaga() {
  yield* all([
    takeLatest(
      Actions.DELETE_REDACTION_CODE_REQUEST,
      deleteRedactionCodeRequest
    ),
    takeLatest(
      Actions.DELETE_REDACTION_CODE_SUCCESS,
      deleteRedactionCodeSuccess
    ),
    takeLatest(
      Actions.DELETE_REDACTION_CODE_FAILURE,
      deleteRedactionCodeFailure
    ),
  ]);
}
export function* deleteRedactionCodeRequest(
  action: Action<deleteReactionCodePayload>
) {
  yield* put(Services.deleteRedactionCodeSdo(action.payload));
}

export function* deleteRedactionCodeSuccess() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'deleteRedactionCodeSuccess',
        defaultMessage: 'Deleted redaction code successfully.',
      }),
      variant: 'success',
    })
  );
}

export function* deleteRedactionCodeFailure() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'deleteRedactionCodeFailure',
        defaultMessage: 'An error occurred while deleting redaction code.',
      }),
      variant: 'error',
    })
  );
}

export function* watchSaveRedactionCodeRequestSaga() {
  yield* all([
    takeLatest(Actions.SAVE_REDACTION_CODE_REQUEST, saveRedactionCodeRequest),
    takeLatest(Actions.SAVE_REDACTION_CODE_SUCCESS, saveRedactionCodeSuccess),
    takeLatest(Actions.SAVE_REDACTION_CODE_FAILURE, saveRedactionCodeFailure),
  ]);
}

export function* saveRedactionCodeRequest(
  action: Action<{ redactionCode: RedactionCode }>
) {
  const { redactionCode } = action.payload;
  const selectedEditCode = yield* select(selectSelectedEditCode);
  yield* put(
    Services.checkRedactionCodeExists({
      isEdit: !!selectedEditCode?.id,
      redactionCode,
    })
  );
}

export function* watchCheckRedactionCodeExists() {
  yield* takeLatest(
    CHECK_REDACTION_CODE_EXISTS_SUCCESS,
    checkRedactionCodeExistsSuccess
  );
  yield* takeLatest(
    CHECK_REDACTION_CODE_EXISTS_FAILURE,
    checkRedactionCodeExistsFailure
  );
}
export function* checkRedactionCodeExistsSuccess(
  action: Action<CheckRedactionCodePayload>
) {
  const intl = sagaIntl();
  const { isDuplicate, redactionCode } = action.payload;
  if (isDuplicate) {
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'checkRedactionCodeExistsSuccess',
          defaultMessage:
            'Failed to save. Looks like Redaction Code or Code Name already exists. Please verify and use different Code or Code Name.',
        }),
        variant: 'error',
      })
    );
  } else {
    yield* put(
      Services.saveRedactionCode({
        redactionCode,
      })
    );
  }
}
export function* checkRedactionCodeExistsFailure() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'checkRedactionCodeExistsFailure',
        defaultMessage:
          'An error occurred while performing redaction code exists validation.',
      }),
      variant: 'error',
    })
  );
}
export function* saveRedactionCodeSuccess() {
  const intl = sagaIntl();
  yield* put(actionClearSelectedRedactionCode());
  // An explicit close, not a toggle: a toggle OPENS the drawer whenever the
  // save came from anywhere but the drawer itself (VE-28768).
  yield* put(Actions.actionSetIsDrawerOpen({ isOpen: false }));
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'saveRedactionCodeSuccess',
        defaultMessage: 'Saved Redaction Code successfully.',
      }),
      variant: 'success',
    })
  );
}

export function* saveRedactionCodeFailure() {
  const intl = sagaIntl();
  yield* put(
    enqueueSnackbar({
      message: intl.formatMessage({
        id: 'savedRedactionCodeFailure',
        defaultMessage: 'An error occurred while saving redaction code.',
      }),
      variant: 'error',
    })
  );
}

export function* fetchCountRedactionCodes() {
  // TODO: Should FETCH_COUNT_REDACTION_CODES be here, or should it be FETCH_COUNT_REDACTION_CODES_SUCCESS?????
  // yield* takeEvery(
  //   [
  //     Actions.FETCH_COUNT_REDACTION_CODES,
  //     Actions.DELETE_REDACTION_CODE_SUCCESS,
  //     Actions.SAVE_REDACTION_CODE_SUCCESS,
  //   ],
  yield* takeEvery(
    [
      Actions.FETCH_COUNT_REDACTION_CODES,
      Actions.DELETE_REDACTION_CODE_SUCCESS,
      Actions.SAVE_REDACTION_CODE_SUCCESS,
    ],
    function* () {
      yield* put(Services.fetchTotalRedactionCodes());
    }
  );
}
